/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// depletionService :: depletions + inventory over RLS (Supabase Auth).
//
// Ported from the source app, where every call went through a service-role
// `depletion` edge function (Azure-AD model). Brand Command uses Supabase Auth
// + RLS, so — like forecastService / referenceDataService — we talk to the
// tables DIRECTLY with the authenticated client. RLS (migration 0033) pins
// every row to the caller's account via is_account_member(account_id).
//
// Row shapes returned here match what the ported pages expect: each loaded row
// carries nested `states` / `flavors` / `sizes` / `pack_sizes` objects (the same
// envelope the source service produced), so the components are
// unchanged apart from uuid ids (no Number() coercion) and the auth layer.

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { fetchAllRows } from "../../../shared/utils/fetchAllRows.js";

// ── Active account (the tenant whose data we manage) ─────────────────────────
let _accountIdPromise = null;

export function resetDepletionAccount() {
  _accountIdPromise = null;
}

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// Joined select used by both load paths — yields the source app's nested envelope.
// chain_name / account_name / account_address / account_city are the retail
// account (outlet/door) detail. Optional: a state-level sheet leaves them NULL
// and every screen degrades to the old state-level view.
const DEPLETION_SELECT =
  "id, year, month, case_sales, " +
  "chain_name, account_name, account_address, account_city, " +
  "states:sales_states(id, state_name, region, distributor), " +
  "flavors:product_flavors(id, name), " +
  "sizes:product_sizes(id, name), " +
  "pack_sizes:pack_sizes(id, name)";

const INVENTORY_SELECT =
  "id, current_inventory, " +
  "states:sales_states(id, state_name, region, distributor), " +
  "flavors:product_flavors(id, name), " +
  "sizes:product_sizes(id, name), " +
  "pack_sizes:pack_sizes(id, name)";

// ── Depletions ───────────────────────────────────────────────────────────────

/** Load depletion rows, optionally filtered. ids are uuid strings. */
export async function loadDepletions({ year, stateId, flavorId, sizeId, packSizeId, chainName, accountName } = {}) {
  const acct = await activeAccountId();
  const build = () => {
    let q = supabase.from("depletions").select(DEPLETION_SELECT).eq("account_id", acct);
    if (year != null && year !== "") q = q.eq("year", Number(year));
    if (stateId)    q = q.eq("state_id", stateId);
    if (flavorId)   q = q.eq("flavor_id", flavorId);
    if (sizeId)     q = q.eq("size_id", sizeId);
    if (packSizeId) q = q.eq("pack_size_id", packSizeId);
    if (chainName)   q = q.eq("chain_name", chainName);
    if (accountName) q = q.eq("account_name", accountName);
    return q.order("id", { ascending: true });
  };
  try {
    return await fetchAllRows(build, { label: "depletions" });
  } catch (error) {
    console.error("[depletionService.loadDepletions]", error);
    throw new Error("Could not load depletions.");
  }
}

/** Raw rows (no joins) across the given years — used by loadAvgMonthlySales. */
async function loadDepletionsByYears(years) {
  if (!Array.isArray(years) || years.length === 0) return [];
  const acct = await activeAccountId();
  try {
    return await fetchAllRows(
      () => supabase
        .from("depletions")
        .select("year, month, state_id, flavor_id, size_id, pack_size_id, case_sales")
        .eq("account_id", acct)
        .in("year", years.map(Number))
        .order("id", { ascending: true }),
      { label: "depletions (by year)" }
    );
  } catch (error) {
    console.error("[depletionService.loadDepletionsByYears]", error);
    throw new Error("Could not load depletions.");
  }
}

/**
 * Bulk-insert depletion rows. Each:
 *   { year, month, stateId, flavorId, sizeId, packSizeId, caseSales,
 *     chainName?, accountName?, accountAddress?, accountCity? }
 *
 * The four retail-account (outlet/door) fields are OPTIONAL and mirror what the
 * sheets-sync edge function captures, so a file imported here and the same file
 * synced from Google Sheets produce identical rows. Without them the Import path
 * would silently drop store detail the Sheets path keeps.
 *
 * Explicit column whitelist per SECURITY_RULES.md — DB-controlled fields (id,
 * account_id, timestamps) are never taken from the caller.
 */
const textCell = (v, cap) => {
  const s = v == null ? "" : String(v).trim().slice(0, cap);
  return s || null;
};

/** Column whitelist for a depletion write. Shared by the plain insert and the
 *  write-then-swap importer so both send exactly the same shape. */
function depletionPayload(rows, acct) {
  return rows.map((r) => ({
    account_id:   acct,
    year:         Number(r.year),
    month:        Number(r.month),
    state_id:     r.stateId    || null,
    flavor_id:    r.flavorId   || null,
    size_id:      r.sizeId     || null,
    pack_size_id: r.packSizeId || null,
    case_sales:   r.caseSales != null && r.caseSales !== "" ? Number(r.caseSales) : null,
    chain_name:      textCell(r.chainName, 160),
    account_name:    textCell(r.accountName, 200),
    account_address: textCell(r.accountAddress, 300),
    account_city:    textCell(r.accountCity, 120),
  }));
}

export async function insertDepletions(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return { inserted: 0 };
  const acct = await activeAccountId();
  const payload = depletionPayload(rows, acct);
  const { error } = await supabase.from("depletions").insert(payload);
  if (error) { console.error("[depletionService.insertDepletions]", error); throw new Error("Could not import depletions."); }
  return { inserted: payload.length };
}

/*
 * ⚠️ There is deliberately no clearDepletionPeriods() here any more.
 *
 * It used to exist, exported, next to insertDepletions() above — and pairing
 * the two is precisely the APP-334 data-loss bug: clear-then-write loses the
 * period outright if the write then fails. The docblock said "do not pair
 * these", which is advice, and advice does not survive the next person in a
 * hurry. It had zero callers once importDepletionsSafely() landed, so it is
 * gone (backlog APP-614) and the rule is now mechanical: there is nothing to
 * pair with. A file import goes through importDepletionsSafely() below, which
 * writes first and only then retires the rows it supersedes.
 *
 * Do not reintroduce a period-clear helper. If a genuine "delete these months"
 * user action is ever needed, deleteDepletionsByFilter() below is the deletion
 * primitive and it is not an import path. It refuses to run without a real
 * filter (APP-630), so it cannot be turned into one by accident — but it is
 * still a delete, and a delete before a write is the APP-334 shape whatever
 * else you scope it to. Never call it from an import.
 */

export async function deleteDepletion(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("depletions").delete().eq("account_id", acct).eq("id", id);
  if (error) { console.error("[depletionService.deleteDepletion]", error); throw new Error("Could not delete the row."); }
  return { id };
}

/**
 * Delete depletion rows matching a filter. THE FILTER IS THE SAFETY.
 *
 * Every argument is optional in SHAPE, so this used to accept a call with no
 * filter at all — or with `year`/`month`/`stateId` all empty strings, which is
 * exactly what an unfilled `<select>` hands you — and build a delete scoped to
 * `account_id` alone, wiping the account's entire depletion history (APP-630).
 * It had no callers, which is precisely the state clearDepletionPeriods() was
 * in before someone paired it with an insert and produced APP-334. Consistent
 * with how APP-614 was handled above, the rule is mechanical rather than
 * advisory: the dangerous call is now impossible to write by accident.
 *
 *   deleteDepletionsByFilter({ year: 2025 })              → that year
 *   deleteDepletionsByFilter({ year: "", month: "" })     → throws
 *   deleteDepletionsByFilter({})                          → throws
 *   deleteDepletionsByFilter({ confirmDeleteAll: true })  → the whole account
 *
 * ⚠️ Empty string is NOT absent, and "absent" is what widens the delete.
 * `if (year)` and `if (year != null)` disagree about `""` (and about `0`), so a
 * filter counts as REAL only if it survives the same coercion the query applies.
 * A value that was supplied but does not coerce — `"twenty-25"`, `NaN` — throws
 * rather than falling through as "no year given"; degrading a bad filter into
 * no filter is the whole bug in a new costume.
 *
 * Every check runs BEFORE the first `await`, so a refused call touches nothing.
 */
export async function deleteDepletionsByFilter({ year, month, stateId, confirmDeleteAll = false } = {}) {
  const supplied = (v) => v != null && v !== "";
  const asInt = (v) => {
    const n = Number(v);
    return Number.isInteger(n) ? n : null;
  };

  const y = supplied(year) ? asInt(year) : null;
  const m = supplied(month) ? asInt(month) : null;
  const s = typeof stateId === "string" && stateId.trim() ? stateId.trim() : null;

  if (supplied(year) && y === null) throw new Error("Could not delete depletions: that year is not a whole number.");
  if (supplied(month) && m === null) throw new Error("Could not delete depletions: that month is not a whole number.");
  if (y === null && m === null && s === null && confirmDeleteAll !== true) {
    throw new Error(
      "Refusing to delete every depletion row. deleteDepletionsByFilter() needs a year, a month or a state — " +
      "pass { confirmDeleteAll: true } only if erasing the account's whole depletion history really is the intent."
    );
  }

  const acct = await activeAccountId();
  let q = supabase.from("depletions").delete().eq("account_id", acct);
  if (y !== null) q = q.eq("year", y);
  if (m !== null) q = q.eq("month", m);
  if (s !== null) q = q.eq("state_id", s);
  const { error } = await q;
  if (error) { console.error("[depletionService.deleteDepletionsByFilter]", error); throw new Error("Could not delete depletions."); }
  return { ok: true };
}

/**
 * Map of { "stateId|flavorId|sizeId|packSizeId": avgCaseSalesPerMonth } averaged
 * over the last `mohMonths` calendar months. If today is >= 7 days into the
 * current month, that partial month is pro-rated; otherwise it is skipped and
 * the previous N complete months are used. (Verbatim from the the source app service.)
 */
export async function loadAvgMonthlySales(mohMonths = 3) {
  const today       = new Date();
  const dayOfMonth  = today.getDate();
  const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

  const includeCurrentMonth = dayOfMonth >= 7;
  const startOffset = includeCurrentMonth ? 0 : 1;
  const periods = [];
  for (let i = startOffset; i < startOffset + mohMonths; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    periods.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }

  const periodSet    = new Set(periods.map((p) => `${p.year}-${p.month}`));
  const yearSet      = [...new Set(periods.map((p) => p.year))];
  const currentYear  = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const rawRows = await loadDepletionsByYears(yearSet);
  const relevant = (rawRows || []).filter((r) => periodSet.has(`${r.year}-${r.month}`));

  const totals = {};
  for (const row of relevant) {
    const key = `${row.state_id}|${row.flavor_id}|${row.size_id}|${row.pack_size_id}`;
    let sales = Number(row.case_sales) || 0;
    if (includeCurrentMonth && row.year === currentYear && row.month === currentMonth) {
      sales = sales * (daysInMonth(row.year, row.month) / dayOfMonth);
    }
    totals[key] = (totals[key] ?? 0) + sales;
  }

  const avgs = {};
  for (const [key, total] of Object.entries(totals)) avgs[key] = total / mohMonths;
  return avgs;
}

// ── Inventory ────────────────────────────────────────────────────────────────

export async function loadInventory({ stateId, flavorId, sizeId, packSizeId } = {}) {
  const acct = await activeAccountId();
  const build = () => {
    let q = supabase.from("inventory").select(INVENTORY_SELECT).eq("account_id", acct);
    if (stateId)    q = q.eq("state_id", stateId);
    if (flavorId)   q = q.eq("flavor_id", flavorId);
    if (sizeId)     q = q.eq("size_id", sizeId);
    if (packSizeId) q = q.eq("pack_size_id", packSizeId);
    return q.order("id", { ascending: true });
  };
  try {
    return await fetchAllRows(build, { label: "inventory" });
  } catch (error) {
    console.error("[depletionService.loadInventory]", error);
    throw new Error("Could not load inventory.");
  }
}

/*
 * ⚠️ No clearInventory() either, and for the same reason — see the note above
 * where clearDepletionPeriods() used to be (backlog APP-614). Wiping the
 * account's whole inventory and then inserting is the APP-334 shape at its
 * worst: the blast radius is every row the tenant has, not one month.
 * importInventorySafely() below writes the new snapshot first and retires the
 * old ids only once it has landed.
 */

function inventoryPayload(rows, acct) {
  return rows.map((r) => ({
    account_id:        acct,
    state_id:          r.stateId    || null,
    flavor_id:         r.flavorId   || null,
    size_id:           r.sizeId     || null,
    pack_size_id:      r.packSizeId || null,
    current_inventory: r.currentInventory != null && r.currentInventory !== "" ? Number(r.currentInventory) : null,
  }));
}

/** rows: [{ stateId, flavorId, sizeId, packSizeId, currentInventory }] */
export async function insertInventory(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return { inserted: 0 };
  const acct = await activeAccountId();
  const payload = inventoryPayload(rows, acct);
  const { error } = await supabase.from("inventory").insert(payload);
  if (error) { console.error("[depletionService.insertInventory]", error); throw new Error("Could not import inventory."); }
  return { inserted: payload.length };
}

// ── Distributor "last updated" tracker ───────────────────────────────────────

async function loadDistributorLastUpdated(distributor) {
  const acct = await activeAccountId();
  let q = supabase
    .from("distributor_last_updated")
    .select("distributor, last_depletions_update, last_inventory_update, last_depletions_modified, last_inventory_modified")
    .eq("account_id", acct)
    .order("distributor", { ascending: true });
  if (distributor) q = q.eq("distributor", distributor);
  const { data, error } = await q;
  if (error) { console.error("[depletionService.loadDistributorLastUpdated]", error); throw new Error("Could not load distributor status."); }
  return data ?? [];
}

export async function loadDistributorLastUpdatedDepletions(distributor) {
  return loadDistributorLastUpdated(distributor);
}

export async function loadDistributorLastUpdatedInventory(distributor) {
  return loadDistributorLastUpdated(distributor);
}

/** Stamp the refresh tracker for the given distributors after an import. */
async function markDistributors(distributors, columns) {
  const names = [...new Set((distributors || []).map((d) => String(d || "").trim()).filter(Boolean))];
  if (names.length === 0) return;
  const acct = await activeAccountId();
  const now = new Date().toISOString();
  const rows = names.map((distributor) => ({ account_id: acct, distributor, ...columns(now) }));
  const { error } = await supabase
    .from("distributor_last_updated")
    .upsert(rows, { onConflict: "account_id,distributor" });
  if (error) console.error("[depletionService.markDistributors]", error); // non-fatal
}

export async function markDepletionsUpdated(distributors) {
  return markDistributors(distributors, (now) => ({ last_depletions_update: now, last_depletions_modified: now }));
}

export async function markInventoryUpdated(distributors) {
  return markDistributors(distributors, (now) => ({ last_inventory_update: now, last_inventory_modified: now }));
}

// ── File import: WRITE-THEN-SWAP (backlog APP-334) ───────────────────────────
//
// THE BUG THIS REPLACES: the Import modal used to call clearDepletionPeriods()
// / clearInventory() (both since deleted — APP-614) and THEN insert. If the
// insert failed partway — dropped connection, one batch rejected — the periods
// were already deleted and the user was told only "Import failed.". The data
// was gone and nothing said so.
//
// There is no transaction available from the browser (a transactional RPC needs
// a migration), so the order is inverted instead:
//
//   1. SNAPSHOT the ids the import is going to supersede (ids only — cheap).
//   2. INSERT the new rows in chunks, keeping the id of every row that lands.
//   3. Only once every chunk has landed, DELETE the snapshot ids.
//
// At no point does the account's data stop existing. The schema permits this:
// `depletions` and `inventory` have no unique constraint beyond the uuid pk
// (migration 0033), so old and new rows coexist for the seconds between 2 and 3.
//
// Every failure mode is therefore recoverable AND is reported as one of four
// honest outcomes (see IMPORT_OUTCOME) rather than a bare "Import failed.":
//   replaced             success — new rows in, superseded rows gone
//   intact               nothing was written; the existing data is untouched
//   intact-plus-orphans  write failed and some new rows could not be taken back
//   replaced-plus-stale  new rows are in, some superseded rows could not be removed
// The last two double-count until fixed, which is visible and repairable —
// re-running the same import supersedes both copies and self-heals.

const IMPORT_INSERT_CHUNK = 500;   // rows per insert request
const ID_DELETE_CHUNK = 100;       // uuids per delete request (URL-length safe)
const ID_DELETE_CONCURRENCY = 4;
const PERIOD_FILTER_CHUNK = 40;    // (year,month) pairs per or() filter

export const IMPORT_OUTCOME = {
  REPLACED: "replaced",
  INTACT: "intact",
  INTACT_PLUS_ORPHANS: "intact-plus-orphans",
  REPLACED_PLUS_STALE: "replaced-plus-stale",
  UNKNOWN: "unknown",
};

const chunked = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * `or=(and(year.eq.2025,month.eq.1),…)`.
 *
 * SECURITY_RULES.md #3 — a filter STRING is PostgREST syntax, not a bound
 * value, so nothing raw goes in it: every year/month is coerced and must be an
 * integer, and an empty list throws rather than emitting `or=()`, which would
 * widen the match on the query whose result set gets DELETED.
 */
function periodOrFilter(periods) {
  if (!Array.isArray(periods) || periods.length === 0) throw new Error("No periods.");
  return periods
    .map((p) => {
      const y = Number(p.year), m = Number(p.month);
      if (!Number.isInteger(y) || !Number.isInteger(m)) throw new Error("Bad period.");
      return `and(year.eq.${y},month.eq.${m})`;
    })
    .join(",");
}

/**
 * ids of the depletion rows the given periods currently hold.
 *
 * Reads year/month back and re-checks them in JS: these ids are what the swap
 * DELETES, so a filter that silently matched too much would be the data-loss
 * bug again in a new costume. Belt and braces on the one query that can hurt.
 */
async function readDepletionIdsInPeriods(periods) {
  const acct = await activeAccountId();
  const want = new Set(periods.map((p) => `${Number(p.year)}-${Number(p.month)}`));
  const ids = [];
  try {
    for (const group of chunked(periods, PERIOD_FILTER_CHUNK)) {
      const filter = periodOrFilter(group);
      const rows = await fetchAllRows(
        () => supabase
          .from("depletions").select("id, year, month")
          .eq("account_id", acct).or(filter)
          .order("id", { ascending: true }),
        // strict: this list is what the swap DELETES. A truncated list would
        // leave superseded rows in place and still report success (APP-334
        // review finding 2), which is the double-count bug in a new costume.
        { label: "depletions (period ids)", strict: true }
      );
      for (const r of rows) {
        if (want.has(`${r.year}-${r.month}`)) ids.push(r.id);
      }
    }
  } catch (error) {
    console.error("[depletionService.readDepletionIdsInPeriods]", error);
    throw new Error("Could not read the existing months for those dates.");
  }
  return ids;
}

async function readInventoryIds() {
  const acct = await activeAccountId();
  try {
    const rows = await fetchAllRows(
      () => supabase.from("inventory").select("id").eq("account_id", acct).order("id", { ascending: true }),
      { label: "inventory (ids)", strict: true }   // see readDepletionIdsInPeriods
    );
    return rows.map((r) => r.id);
  } catch (error) {
    console.error("[depletionService.readInventoryIds]", error);
    throw new Error("Could not read the existing inventory.");
  }
}

/** Delete by id, chunked. Never throws — a partial delete is a REPORTABLE state,
 *  not an exception, because the caller has to tell the user which rows remain. */
async function deleteRowsByIds(table, ids) {
  if (!ids.length) return { removed: 0, failedIds: [] };
  const acct = await activeAccountId();
  const groups = chunked(ids, ID_DELETE_CHUNK);
  let removed = 0;
  const failedIds = [];
  for (const wave of chunked(groups, ID_DELETE_CONCURRENCY)) {
    const results = await Promise.allSettled(
      wave.map(async (g) => {
        const { error } = await supabase.from(table).delete().eq("account_id", acct).in("id", g);
        if (error) throw error;
        return g.length;
      })
    );
    results.forEach((res, i) => {
      if (res.status === "fulfilled") removed += res.value;
      else {
        failedIds.push(...wave[i]);
        console.error(`[depletionService.deleteRowsByIds:${table}]`, res.reason);
      }
    });
  }
  return { removed, failedIds };
}

/**
 * Insert `payload` in chunks, remembering the id of every row that lands.
 * Never throws; returns { ok, insertedIds, unaccounted, landed, unproven }.
 *
 * On a chunk error it asks `countRows()` how many rows are actually there. A
 * PostgREST array insert is ONE statement, so it is all-or-nothing — the count
 * settles whether the chunk landed and the response was merely lost:
 *   • count == expected-after  → it landed. Continue, but we have no ids for it
 *     (`unaccounted`), so a later rollback cannot take those rows back.
 *   • count == expected-before → proven not landed. Retry once.
 *   • anything else            → someone else is writing. Stop; never guess.
 * A blind retry is what would silently double-count, since nothing in the
 * schema would reject the second copy.
 *
 * ⚠️ `unproven` is the difference between "we know it didn't land" and "we
 * couldn't find out" (APP-334 review finding 1). Both exit through `ok:false`,
 * but only the first may be reported as INTACT. If the probe itself fails — the
 * same dropped connection that lost the insert response will usually also lose
 * the probe — the chunk may well BE in the table with no ids to take it back
 * out, and telling the user "nothing was written, safe to try again" is then a
 * lie that produces a duplicate on the retry. Collapsing those two states into
 * one is exactly the class of bug this whole function exists to prevent.
 */
async function insertChunksVerified({ table, payload, countRows, baseline }) {
  const insertedIds = [];
  let landed = 0, unaccounted = 0, unproven = false;

  for (const group of chunked(payload, IMPORT_INSERT_CHUNK)) {
    let done = false;
    for (let attempt = 0; attempt < 2 && !done; attempt++) {
      const { data, error } = await supabase.from(table).insert(group).select("id");
      if (!error) {
        const got = (data || []).map((r) => r.id);
        insertedIds.push(...got);
        // The chunk is sized under PostgREST's 1000-row response cap so every
        // id comes back. If one ever doesn't, that row exists and we cannot
        // take it back — count it rather than quietly losing track of it.
        if (got.length < group.length) unaccounted += group.length - got.length;
        done = true;
        break;
      }
      console.error(`[depletionService.insertChunksVerified:${table}]`, error);
      let observed = null;
      try { observed = await countRows(); }
      catch (probeError) {
        console.error(`[depletionService.insertChunksVerified:${table}] probe`, probeError);
        unproven = true;                         // can't tell → don't retry, don't claim
        break;
      }
      if (observed === baseline + landed + group.length) { unaccounted += group.length; done = true; break; }
      if (observed !== baseline + landed) {
        unproven = true;                         // ambiguous → don't retry, don't claim
        break;
      }
      if (attempt === 0) await sleep(500);
    }
    if (!done) return { ok: false, insertedIds, unaccounted, landed, unproven };
    landed += group.length;
  }
  return { ok: true, insertedIds, unaccounted, landed, unproven: false };
}

/** Shared tail: new rows are in, so retire the ones they supersede. */
async function swapOut(table, oldIds) {
  const swap = await deleteRowsByIds(table, oldIds);
  return swap.failedIds.length
    ? { outcome: IMPORT_OUTCOME.REPLACED_PLUS_STALE, stale: swap.failedIds.length }
    : { outcome: IMPORT_OUTCOME.REPLACED, stale: 0 };
}

/** Shared tail: the write failed, so take back whatever it managed to write.
 *  Nothing has been deleted at this point either way — the swap is downstream —
 *  so the user's own data is safe in every branch. What differs is what we can
 *  honestly say about the NEW rows. */
async function rollBack(table, write) {
  const back = await deleteRowsByIds(table, write.insertedIds);
  const orphans = back.failedIds.length + write.unaccounted;
  // Unproven beats both: we do not know whether the last chunk is in the table,
  // so we can claim neither "nothing was written" nor a specific orphan count.
  if (write.unproven) return { outcome: IMPORT_OUTCOME.UNKNOWN, orphans };
  return orphans
    ? { outcome: IMPORT_OUTCOME.INTACT_PLUS_ORPHANS, orphans }
    : { outcome: IMPORT_OUTCOME.INTACT, orphans: 0 };
}

/**
 * Replace the given periods with `rows`, without ever leaving them empty.
 * Returns { ok, outcome, inserted, superseded, stale, orphans } — it only
 * throws BEFORE anything is written, in which case the data is untouched.
 */
export async function importDepletionsSafely({ rows, periods, distributors }) {
  if (!Array.isArray(rows) || rows.length === 0) return { ok: true, outcome: IMPORT_OUTCOME.REPLACED, inserted: 0, superseded: 0, stale: 0, orphans: 0 };
  const acct = await activeAccountId();                       // pre-write: may throw
  const payload = depletionPayload(rows, acct);
  // Every period is validated, NOT filtered. `countRows()` counts rows within
  // cleanPeriods, so the landed/not-landed arithmetic below is only sound if
  // every payload row's (year, month) is in that set. Dropping a malformed
  // period would quietly shrink the count window and make an insert that DID
  // land look like one that didn't — which retries and duplicates (APP-334
  // review finding 3). Refuse instead; this is still pre-write, nothing is
  // touched.
  const cleanPeriods = (periods || []).map((p) => {
    const year = Number(p?.year), month = Number(p?.month);
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
      throw new Error(`Could not read the month for one of the rows (${p?.year}/${p?.month}).`);
    }
    return { year, month };
  });
  if (!cleanPeriods.length) throw new Error("Could not work out which months this file covers.");
  const oldIds = await readDepletionIdsInPeriods(cleanPeriods);  // pre-write: may throw

  try {
    const write = await insertChunksVerified({
      table: "depletions",
      payload,
      baseline: oldIds.length,
      countRows: async () => (await readDepletionIdsInPeriods(cleanPeriods)).length,
    });

    if (!write.ok) {
      const { outcome, orphans } = await rollBack("depletions", write);
      return { ok: false, outcome, inserted: 0, superseded: 0, stale: 0, orphans };
    }

    const { outcome, stale } = await swapOut("depletions", oldIds);
    await markDepletionsUpdated(distributors);                 // already non-fatal
    return {
      ok: outcome === IMPORT_OUTCOME.REPLACED,
      outcome, inserted: payload.length, superseded: oldIds.length - stale, stale, orphans: 0,
    };
  } catch (error) {
    // Defensive: past this point rows may or may not be in. Say exactly that
    // rather than implying either.
    console.error("[depletionService.importDepletionsSafely]", error);
    return { ok: false, outcome: IMPORT_OUTCOME.UNKNOWN, inserted: 0, superseded: 0, stale: 0, orphans: 0 };
  }
}

/** Inventory is a full snapshot, so the superseded set is the whole account. */
export async function importInventorySafely({ rows, distributors }) {
  if (!Array.isArray(rows) || rows.length === 0) return { ok: true, outcome: IMPORT_OUTCOME.REPLACED, inserted: 0, superseded: 0, stale: 0, orphans: 0 };
  const acct = await activeAccountId();
  const payload = inventoryPayload(rows, acct);
  const oldIds = await readInventoryIds();

  try {
    const write = await insertChunksVerified({
      table: "inventory",
      payload,
      baseline: oldIds.length,
      countRows: async () => (await readInventoryIds()).length,
    });

    if (!write.ok) {
      const { outcome, orphans } = await rollBack("inventory", write);
      return { ok: false, outcome, inserted: 0, superseded: 0, stale: 0, orphans };
    }

    const { outcome, stale } = await swapOut("inventory", oldIds);
    await markInventoryUpdated(distributors);
    return {
      ok: outcome === IMPORT_OUTCOME.REPLACED,
      outcome, inserted: payload.length, superseded: oldIds.length - stale, stale, orphans: 0,
    };
  } catch (error) {
    console.error("[depletionService.importInventorySafely]", error);
    return { ok: false, outcome: IMPORT_OUTCOME.UNKNOWN, inserted: 0, superseded: 0, stale: 0, orphans: 0 };
  }
}
